<template>
  <div class="css-section">
    <!-- Grupo 1: Proveedores -->
    <section class="css-group">
      <span class="css-group-title">Proveedores de servicios</span>
      <span class="css-group-sub">Los proveedores registrados aquí aparecen como sugerencias al registrar facturas en la página de servicios.</span>
      <div v-if="providers.length" class="css-search-wrap">
        <span class="mdi mdi-magnify css-search-icon"></span>
        <input v-model="providerSearch" class="css-search-input" placeholder="Buscar por nombre, RFC o contacto..." />
        <button v-if="providerSearch" class="css-search-clear" @click="providerSearch = ''"><span class="mdi mdi-close"></span></button>
      </div>

      <div v-if="!providers.length && !showAddForm" class="css-empty">
        <span class="mdi mdi-office-building-outline css-empty-icon"></span>
        <span class="css-empty-title">Sin proveedores registrados</span>
        <span class="css-empty-sub">Agrega tus proveedores de servicios para que aparezcan como sugerencias al registrar facturas</span>
        <button class="css-save-btn" @click="startAdd">Agregar primer proveedor</button>
      </div>

      <template v-else>
        <div v-for="p in filteredProviders" :key="p.id" class="css-provider-card">
          <template v-if="editingId !== p.id">
            <span class="css-provider-icon" :style="{ background: typeColor(p.category) + '1f', color: typeColor(p.category) }">
              <span class="mdi" :class="typeIcon(p.category)"></span>
            </span>            
            <div class="css-provider-info">
              <div class="css-provider-name">{{ p.name }}</div>
              <span class="css-provider-type-tag" :style="typeTagStyle(p.category)">{{ categoryLabel(p) }}</span>
            </div>
            <div class="css-provider-contact">
              <div v-if="p.phone" class="css-provider-contact-line">
                <span class="mdi mdi-phone-outline"></span> {{ p.phone }}
                <span class="mdi mdi-content-copy css-copy-icon" @click="copyToClipboard(p.phone)"></span>
              </div>
              <div v-if="p.email" class="css-provider-contact-line">
                <span class="mdi mdi-email-outline"></span> {{ p.email }}
                <span class="mdi mdi-content-copy css-copy-icon" @click="copyToClipboard(p.email)"></span>
              </div>
            </div>
            <div class="css-provider-actions">
              <button class="css-icon-btn" @click="startEdit(p)"><span class="mdi mdi-pencil-outline"></span></button>
              <button class="css-icon-btn css-icon-btn--red" @click="onDeleteProvider(p.id)"><span class="mdi mdi-close"></span></button>
            </div>
          </template>

          <div v-else class="css-provider-expanded">
            <div class="css-provider-form-grid">
              <div class="css-provider-form-col">
                <input v-model="form.name" class="css-input" placeholder="Nombre de la empresa" />
                <input v-model="form.rfc" class="css-input" placeholder="RFC" />
                <select v-model="form.category" class="css-input">
                  <option v-for="t in allTypes" :key="t.value || t.id" :value="t.value || `CUSTOM:${t.id}`">{{ t.label || t.name }}</option>
                </select>
              </div>
              <div class="css-provider-form-col">
                <input v-model="form.contactName" class="css-input" placeholder="Nombre del contacto" />
                <input v-model="form.phone" class="css-input" placeholder="Teléfono directo" />
                <input v-model="form.email" class="css-input" placeholder="Correo" />
              </div>
            </div>
            <textarea v-model="form.notes" class="css-textarea" rows="2" placeholder="Notas internas sobre este proveedor..."></textarea>
            <div class="css-provider-form-actions">
              <button class="css-cancel-btn" @click="cancelEdit(p)">Cancelar</button>
              <button class="css-save-btn" @click="saveProvider(p)">Guardar proveedor</button>
            </div>
          </div>
        </div>

        <button v-if="!showAddForm" class="css-add-btn" @click="startAdd">＋ Agregar proveedor</button>
      </template>
    </section>

    <!-- Grupo 2: Tipos de servicio -->
    <section class="css-group">
      <span class="css-group-title">Tipos de servicio</span>
      <span class="css-group-sub">Define los tipos de servicio disponibles en el sistema. Los tipos predefinidos no se pueden eliminar.</span>
      
      <div class="css-chips">
        <span v-for="t in predefinedTypes" :key="t.value" class="css-chip">
          <span class="mdi" :class="typeIcon(t.value)" :style="{ color: typeColor(t.value) }"></span>
          {{ t.label }}
          <span class="mdi mdi-lock-outline css-chip-lock"></span>
        </span>
        <span v-for="t in customTypes" :key="t.id" class="css-chip">
          <span class="mdi mdi-dots-grid" style="color:#94a3b8"></span>
          {{ t.name }}
          <span class="mdi mdi-close css-chip-remove" @click="onDeleteType(t.id)"></span>
        </span>
      </div>

      <div v-if="!addingType" class="css-add-type-row">
        <button class="css-link-btn" @click="addingType = true">＋ Agregar tipo personalizado</button>
      </div>
      <div v-else class="css-add-type-row">
        <input v-model="newTypeName" class="css-input css-type-input" placeholder="Nombre del tipo..." @keyup.enter="confirmAddType" />
        <button class="css-icon-btn" @click="confirmAddType"><span class="mdi mdi-check"></span></button>
        <button class="css-icon-btn" @click="addingType = false; newTypeName = ''"><span class="mdi mdi-close"></span></button>
      </div>
    </section>

    <Transition name="css-toast-fade">
      <div v-if="toastMessage" class="css-toast"><span class="mdi mdi-check-circle"></span> {{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  getProviders, createProvider, updateProvider, deleteProvider,
  getServiceTypes, addCustomServiceType, deleteCustomServiceType,
} from '@/services/parkConfig'

const props = defineProps({ parkId: { type: String, required: true } })
const emit = defineEmits(['dirty-change'])

const CATEGORY_META = {
  ELECTRICITY: { icon: 'mdi-lightning-bolt', color: '#eab308' },
  WATER: { icon: 'mdi-water', color: '#0ea5e9' },
  SECURITY: { icon: 'mdi-shield-outline', color: '#1d4ed8' },
  ROAD_MAINTENANCE: { icon: 'mdi-road-variant', color: '#6b7280' },
  WASTE_COLLECTION: { icon: 'mdi-recycle', color: '#16a34a' },
  LIGHTING: { icon: 'mdi-lightbulb-outline', color: '#f59e0b' },
  GAS: { icon: 'mdi-fire', color: '#ea580c' },
  CUSTOM: { icon: 'mdi-dots-grid', color: '#94a3b8' },
}

const providers = ref([])
const predefinedTypes = ref([])
const customTypes = ref([])
const toastMessage = ref(null)

async function loadProviders() {
  providers.value = await getProviders(props.parkId)
}
async function loadTypes() {
  const result = await getServiceTypes(props.parkId)
  predefinedTypes.value = result.predefined
  customTypes.value = result.custom
}
onMounted(() => { loadProviders(); loadTypes() })

const allTypes = computed(() => [...predefinedTypes.value, ...customTypes.value])

function typeIcon(v) { return CATEGORY_META[v]?.icon || 'mdi-dots-grid' }
function typeColor(v) { return CATEGORY_META[v]?.color || '#94a3b8' }
function typeTagStyle(category) {
  const color = typeColor(category)
  return { background: color + '1f', color }
}
function categoryLabel(p) {
  if (p.category === 'CUSTOM' && p.customCategoryName) return p.customCategoryName
  return predefinedTypes.value.find((t) => t.value === p.category)?.label || p.category
}

function showToast(msg) {
  toastMessage.value = msg
  setTimeout(() => (toastMessage.value = null), 2500)
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text)
  showToast('Copiado al portapapeles')
}

// --- Proveedores ---
const editingId = ref(null)
const showAddForm = ref(false)
const form = ref({ name: '', rfc: '', category: 'ELECTRICITY', customCategoryName: '', contactName: '', phone: '', email: '', notes: '' })
const providerSearch = ref('')

const filteredProviders = computed(() => {
  if (!providerSearch.value.trim()) return providers.value
  const q = providerSearch.value.toLowerCase()
  return providers.value.filter((p) =>
    p.name?.toLowerCase().includes(q) ||
    p.rfc?.toLowerCase().includes(q) ||
    p.contactName?.toLowerCase().includes(q)
  )
})

function startEdit(p) {
  editingId.value = p.id
  form.value = { ...p, category: p.category === 'CUSTOM' ? `CUSTOM:${p.customCategoryName}` : p.category }
  emit('dirty-change', true)
}

function startAdd() {
  showAddForm.value = true
  const newProvider = { id: 'new', name: '', rfc: '', category: 'ELECTRICITY', contactName: '', phone: '', email: '', notes: '' }
  providers.value.push(newProvider)
  startEdit(newProvider)
}

function cancelEdit(p) {
  if (p.id === 'new') {
    providers.value = providers.value.filter((x) => x.id !== 'new')
    showAddForm.value = false
  }
  editingId.value = null
  emit('dirty-change', false)
}

async function saveProvider(p) {
  const payload = { ...form.value }
  if (typeof payload.category === 'string' && payload.category.startsWith('CUSTOM:')) {
    payload.customCategoryName = payload.category.split(':')[1]
    payload.category = 'CUSTOM'
  }
  if (p.id === 'new') {
    await createProvider(props.parkId, payload)
    showAddForm.value = false
  } else {
    await updateProvider(props.parkId, p.id, payload)
  }
  editingId.value = null
  emit('dirty-change', false)
  await loadProviders()
  showToast('Proveedor guardado')
}

async function onDeleteProvider(id) {
  if (!confirm('¿Eliminar este proveedor? No afecta las facturas históricas ya guardadas.')) return
  await deleteProvider(props.parkId, id)
  await loadProviders()
}

// --- Tipos ---
const addingType = ref(false)
const newTypeName = ref('')

async function confirmAddType() {
  if (!newTypeName.value.trim()) return
  await addCustomServiceType(props.parkId, newTypeName.value)
  newTypeName.value = ''
  addingType.value = false
  await loadTypes()
}

async function onDeleteType(typeId) {
  if (!confirm('¿Eliminar este tipo? No afecta los registros históricos que ya lo usan.')) return
  await deleteCustomServiceType(props.parkId, typeId)
  await loadTypes()
}
</script>

<style scoped>
.css-section { display: flex; flex-direction: column; gap: 1.5rem; max-width: 680px; }
.css-group { display: flex; flex-direction: column; gap: 0.6rem; padding-bottom: 1.5rem; border-bottom: 1px solid #f1f5f9; }
.css-group-title { font-family: var(--font-body); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
.css-group-sub { font-family: var(--font-body); font-size: 11px; color: #94a3b8; margin-top: -0.4rem; }

.css-empty { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; padding: 2rem; text-align: center; }
.css-empty-icon { font-size: 32px; color: #cbd5e1; }
.css-empty-title { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #64748b; }
.css-empty-sub { font-family: var(--font-body); font-size: 11px; color: #94a3b8; max-width: 320px; }

.css-provider-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.css-provider-card > div:not(.css-provider-expanded) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 72px;
  padding: 0 14px;
}
.css-provider-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 10px;
  margin-top: 10px;
}
.css-provider-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.15rem; }
.css-provider-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.css-provider-type-tag { font-size: 10px; border-radius: 4px; padding: 0.05rem 0.35rem; width: fit-content; }

.css-provider-contact { display: flex; flex-direction: column; gap: 0.1rem; flex-shrink: 0; }
.css-provider-contact-line { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 11px; color: #64748b; }
.css-copy-icon { font-size: 10px; color: #cbd5e1; cursor: pointer; }

.css-provider-actions { display: flex; flex-direction: column; gap: 0.3rem; flex-shrink: 0; }
.css-icon-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.css-icon-btn--red:hover { color: #dc2626; }

.css-provider-expanded { background: #fafafa; border-top: 1px solid #f1f5f9; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; }
.css-provider-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.css-provider-form-col { display: flex; flex-direction: column; gap: 0.4rem; }
.css-input, .css-textarea { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.5rem; box-sizing: border-box; width: 100%; }
.css-textarea { resize: vertical; }
.css-provider-form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.css-cancel-btn { background: none; border: none; color: #64748b; font-family: var(--font-body); font-size: 12px; cursor: pointer; }

.css-add-btn { height: 48px; background: transparent; border: 1px dashed #cbd5e1; border-radius: 8px; font-family: var(--font-body); font-size: 13px; color: #94a3b8; cursor: pointer; }
.css-add-btn:hover { border-color: #3b82f6; color: #3b82f6; }

.css-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.css-chip { display: inline-flex; align-items: center; gap: 0.35rem; height: 36px; background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0 0.75rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.css-chip-lock { font-size: 10px; color: #cbd5e1; }
.css-chip-remove { font-size: 10px; color: #94a3b8; cursor: pointer; }

.css-add-type-row { display: flex; align-items: center; gap: 0.4rem; }
.css-link-btn { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 12px; cursor: pointer; padding: 0; }
.css-type-input { width: 200px; }

.css-save-btn { align-self: flex-start; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }

.css-toast { position: fixed; top: 1rem; right: 1rem; display: flex; align-items: center; gap: 0.4rem; background: #1e3a5f; color: #ffffff; padding: 0.6rem 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 3000; }
.css-toast .mdi { color: #10b981; }
.css-toast-fade-enter-active, .css-toast-fade-leave-active { transition: opacity 300ms ease; }
.css-toast-fade-enter-from, .css-toast-fade-leave-to { opacity: 0; }
.css-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 0.7rem;
}

.css-search-icon {
  color: #94a3b8;
  font-size: 14px;
}

.css-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
}

.css-search-clear {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.css-search-empty {
  text-align: center;
  padding: 1.5rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #94a3b8;
}
</style>